import dayjs from 'dayjs';
import clsx from 'clsx';
import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CustomRange } from './CustomRange';
import { PeriodCalendar } from './PeriodCalendar';
import { Icon } from '../../components/ui';
import { todayISO } from '../../lib/format';
import type { DashGranularity } from './api';
import './dateFilter.css';

/** What the picker can be on: a standard period, or a range typed in as a From and a To date. */
export type PickerPeriod = DashGranularity | 'custom';

export const PERIODS: Array<[DashGranularity, string]> = [['day', 'Day'], ['week', 'Week'], ['month', 'Month'], ['year', 'Year']];
const UNIT: Record<DashGranularity, 'day' | 'week' | 'month' | 'year'> = { day: 'day', week: 'week', month: 'month', year: 'year' };
const FMT = 'YYYY-MM-DD';

const isPeriod = (v: string | null): v is DashGranularity => PERIODS.some(([k]) => k === v);

/** First day of the day / ISO week (Monday) / month / financial year (April) that holds `d`. */
export function periodStart(d: dayjs.Dayjs, p: DashGranularity): dayjs.Dayjs {
  if (p === 'day') return d.startOf('day');
  if (p === 'week') return d.subtract((d.day() + 6) % 7, 'day').startOf('day');
  if (p === 'month') return d.startOf('month');
  return dayjs(new Date(d.month() >= 3 ? d.year() : d.year() - 1, 3, 1));
}

/** The first and last day (inclusive) of the day / ISO week / month / financial year that holds `date`. */
export function periodRange(period: DashGranularity, date: string): { from: string; to: string } {
  const start = periodStart(dayjs(date), period);
  return { from: start.format(FMT), to: start.add(1, UNIT[period]).subtract(1, 'day').format(FMT) };
}

/**
 * The period a picked range stands for, by its length: one day is a Day, up to a week (22 to 29 Oct, or the last
 * 7 days) a Week, up to a month (29 Sep to 29 Oct) a Month, and anything longer (29 Oct 2025 to 29 Oct 2026) a Year.
 * `date` is the end of the range, which is where the period is taken from.
 */
export function fitPeriod(from: string, to: string): { period: DashGranularity; date: string } {
  const a = dayjs(from);
  const period: DashGranularity = from === to ? 'day' : !dayjs(to).isAfter(a.add(7, 'day')) ? 'week' : !dayjs(to).isAfter(a.add(1, 'month')) ? 'month' : 'year';
  return { period, date: to };
}

/**
 * The dashboard's period and date live in the address (?period=week&date=2026-10-14) so a refresh or a shared link
 * shows the same view. With neither, it is the financial year to date. The date is never later than today.
 */
export function useDashRange() {
  const [params, setParams] = useSearchParams();
  const today = todayISO();
  const rawPeriod = params.get('period');
  const period: DashGranularity = isPeriod(rawPeriod) ? rawPeriod : 'year';
  const rawDate = params.get('date');
  const date = rawDate && dayjs(rawDate, FMT, true).isValid() && rawDate <= today ? rawDate : today;
  // exact dates picked in the calendar (?from=…&to=…); anything that is not a real, ordered pair up to today is ignored
  const rawFrom = params.get('from');
  const rawTo = params.get('to');
  const ok = (v: string | null): v is string => !!v && dayjs(v, FMT, true).isValid() && v <= today;
  const custom = ok(rawFrom) && ok(rawTo) && rawFrom <= rawTo ? { from: rawFrom, to: rawTo } : null;
  // a range that was typed in as From and To dates (?custom=1): the Custom button is on, whatever length it is
  const isCustom = !!custom && params.get('custom') === '1';

  const set = (next: { period?: DashGranularity; date?: string; range?: { from: string; to: string }; custom?: boolean }) =>
    setParams(
      (prev) => {
        const out = new URLSearchParams(prev);
        const p = next.period ?? period;
        const d = next.date ?? date;
        out.delete('from');
        out.delete('to');
        out.delete('custom');
        if (next.range) {
          out.set('from', next.range.from);
          out.set('to', next.range.to);
          out.set('period', p);
          out.set('date', next.range.to);
          if (next.custom) out.set('custom', '1');
        } else if (p === 'year' && d === today) {
          out.delete('period');
          out.delete('date');
        } else {
          out.set('period', p);
          out.set('date', d);
        }
        return out;
      },
      { replace: true },
    );

  const d = dayjs(date);
  const nextStart = periodStart(d, period).add(1, UNIT[period]);
  const step = (dir: -1 | 1) => {
    const moved = d.add(dir, UNIT[period]);
    set({ date: (moved.isAfter(today) ? dayjs(today) : moved).format(FMT) });
  };
  return {
    period, date, today, set, custom, isCustom,
    /** what the picker shows as on: Custom for typed-in dates, otherwise the period */
    mode: (isCustom ? 'custom' : period) as PickerPeriod,
    /** the query the dashboards are asked with */
    query: custom ? { period, from: custom.from, to: custom.to } : { period, date },
    setPeriod: (p: DashGranularity) => set({ period: p }),
    setDate: (v: string) => v && set({ date: v > today ? today : v }),
    prev: () => step(-1),
    next: () => step(1),
    canNext: !nextStart.isAfter(today),
  };
}

/** What the date box shows: the day, the week's first and last day, the month, or the financial year. */
function chipLabel(period: DashGranularity | null, date: string): string {
  if (!period || period === 'day') return dayjs(date).format('DD-MM-YYYY');
  const { from, to } = periodRange(period, date);
  if (period === 'week') return `${dayjs(from).format('DD-MM-YYYY')}  -  ${dayjs(to).format('DD-MM-YYYY')}`;
  if (period === 'month') return dayjs(from).format('MMM YYYY');
  return `FY ${dayjs(from).format('YYYY')}–${dayjs(to).format('YY')}`;
}

interface PickerProps {
  /** null = no period is on (the Purchase requests page starts that way); 'custom' = a range typed in as From and To dates */
  period: PickerPeriod | null;
  date: string;
  onChange: (period: DashGranularity | null, date: string) => void;
  groupLabel: string;
  dateLabel: string;
  /** clicking the chosen period again turns it off */
  toggle?: boolean;
  className?: string;
  /** the exact dates being shown, when they are not the whole period (a range picked in the calendar) */
  range?: { from: string; to: string } | null;
  /** called instead of onChange when a range is picked, so the page can use the exact dates. With a page that gives this, a Custom button is offered; it calls with 'custom'. */
  onRange?: (period: PickerPeriod, from: string, to: string) => void;
}

/** Day / Week / Month / Year and the date they are taken at, with step arrows. Used by the dashboard and by the Purchase requests list. */
export function PeriodPicker({ period: chosen, date, onChange, groupLabel, dateLabel, toggle, className, range, onRange }: PickerProps) {
  const [open, setOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const closeCustom = useCallback(() => setCustomOpen(false), []);
  const today = todayISO();
  const custom = chosen === 'custom';
  // the standard period behind the date box; a typed-in range has none, so it cannot be stepped
  const period: DashGranularity | null = chosen && chosen !== 'custom' ? chosen : null;
  const unit = custom ? 'period' : UNIT[period ?? 'day'];
  const canNext = !!period && !periodStart(dayjs(date), period).add(1, UNIT[period]).isAfter(today);
  const step = (dir: -1 | 1) => {
    if (!period) return;
    const moved = dayjs(date).add(dir, UNIT[period]);
    onChange(period, (moved.isAfter(today) ? dayjs(today) : moved).format(FMT));
  };
  // the dates the Custom panel opens with: the range on screen, else the month so far
  // what the date box shows; its spoken name starts with the same text
  const chipText = custom && range ? `${dayjs(range.from).format('DD-MM-YYYY')}  -  ${dayjs(range.to).format('DD-MM-YYYY')}` : chipLabel(period, date);
  const startFrom = range?.from ?? dayjs(today).startOf('month').format(FMT);
  const startTo = range?.to ?? today;
  return (
    <div className={clsx('dash-filter', className)} role="group" aria-label={groupLabel}>
      <div className="df-seg">
        {PERIODS.map(([k, label]) => (
          <button key={k} type="button" aria-pressed={chosen === k} onClick={() => { setCustomOpen(false); onChange(toggle && chosen === k ? null : k, date); }}>{label}</button>
        ))}
        {onRange ? (
          <button type="button" className="df-custom" data-custom-toggle aria-pressed={custom} aria-haspopup="dialog" aria-expanded={customOpen} onClick={() => { setOpen(false); setCustomOpen((o) => !o); }}>Custom</button>
        ) : null}
      </div>
      <div className="df-wrap">
        <div className="df-date">
          <button type="button" aria-label={`Previous ${unit}`} disabled={!period} onClick={() => step(-1)}><Icon name="back" /></button>
          <button
            type="button" className="df-chip" aria-haspopup="dialog" aria-expanded={custom ? customOpen : open}
            aria-label={`${chipText}, ${custom && range ? 'change the dates' : 'open the calendar'}`}
            onClick={() => { if (custom) { setOpen(false); setCustomOpen((o) => !o); } else { setCustomOpen(false); setOpen((o) => !o); } }}
          >
            {chipText}
            <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3.5" y="5" width="17" height="15.5" rx="3" /><path d="M8 3v4M16 3v4M3.5 10h17" /></svg>
          </button>
          <input className="df-native" type="date" tabIndex={-1} aria-label={dateLabel} value={date} max={today} onChange={(e) => e.target.value && onChange(period ?? 'day', e.target.value > today ? today : e.target.value)} />
          <button type="button" aria-label={`Next ${unit}`} disabled={!canNext} onClick={() => step(1)} style={{ transform: 'scaleX(-1)' }}><Icon name="back" /></button>
        </div>
        {customOpen && onRange ? <CustomRange from={startFrom} to={startTo} max={today} onClose={closeCustom} onApply={(from, to) => { onRange('custom', from, to); closeCustom(); }} /> : null}
        {open && !custom ? <PeriodCalendar value={date} period={period} range={range ?? (period ? periodRange(period, date) : null)} max={today} onClose={close} onPickPeriod={period ? (d) => { onChange(period, d > today ? today : d); close(); } : undefined} onPickRange={(from, to) => { const f = fitPeriod(from, to); if (onRange) onRange(f.period, from, to); else onChange(f.period, f.date); close(); }} /> : null}
      </div>
    </div>
  );
}

/** The dashboard's filter: its period and date live in the address. It sits in the top bar on wide screens and at the top of the page elsewhere. */
export function DashboardDateFilter({ className }: { className?: string }) {
  const r = useDashRange();
  return (
    <PeriodPicker
      className={className} period={r.mode} date={r.date} range={r.custom} groupLabel="Dashboard period" dateLabel="Show the dashboard for this date"
      onChange={(p, d) => r.set({ period: p ?? r.period, date: d })}
      // typed-in dates keep the length-based period for the charts' buckets, and remember they were typed in
      onRange={(p, from, to) => (p === 'custom' ? r.set({ period: fitPeriod(from, to).period, range: { from, to }, custom: true }) : r.set({ period: p, range: { from, to } }))}
    />
  );
}
